import React, { useState, useEffect } from 'react';
import { tenantService, userService } from '../services/api';

const MODULES = ['campaigns', 'devices', 'account'];
const LEVELS = ['admin', 'editor', 'viewer'];
// UI-only sentinel for "no grant row on this module". Never sent over the
// wire: the upsert POST has no way to express deletion, so No-access saves
// route through the per-module DELETE endpoint instead (see
// EditPermissionsModal.handleSubmit).
const NO_ACCESS = 'none';

// Which permission modules an editor may show and WRITE for a tenant, derived
// from product enrollment: campaigns iff brands_manager, devices iff
// media_console, account always (workspace administration is not a product).
// Fails CLOSED to account-only when `products` is absent: the server that
// emits products on every tenant row is deployed, so a missing field means a
// stale payload — and a fallback that fails open to all modules would
// re-create the exact blind all-module write this gate exists to stop.
// (TenantDetail's inline derivation fails open by its own display-side
// doctrine; this helper feeds write-path modals, where closed is the only
// safe direction.)
export const enrolledModulesFromProducts = (products) => {
  const enrolled = new Set((products || []).map(p => p.product));
  return MODULES.filter(m => m === 'account'
    || (m === 'campaigns' && enrolled.has('brands_manager'))
    || (m === 'devices' && enrolled.has('media_console')));
};

// Tenant Assignment Modal Component (module-based permissions)
export const TenantAssignmentModal = ({ user, onClose, onSuccess, onError }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [permissions, setPermissions] = useState({ campaigns: 'viewer', devices: 'viewer', account: 'viewer' });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        setLoading(true);
        const response = await tenantService.getAll({ search: searchTerm });
        setTenants(response.data || []);
      } catch (err) {
        console.error('Error fetching tenants:', err);
        onError?.('Failed to load tenants');
      } finally {
        setLoading(false);
      }
    };
    fetchTenants();
  }, [searchTerm, onError]);

  // Enrollment-aware editor: the tenant list rows carry products (V276), so
  // the selected row resolves which module columns exist. Computed in the
  // component body so render and submit agree even after the user switches
  // tenants. Before a tenant is picked (selected undefined) this fails closed
  // to account-only — submit is disabled without a selection anyway.
  const selected = tenants.find(t => t.id === selectedTenant);
  const mods = enrolledModulesFromProducts(selected?.products);

  const handlePermissionChange = (module, level) => {
    setPermissions(prev => ({ ...prev, [module]: level }));
  };

  const setAllPermissions = (level) => {
    setPermissions(prev => {
      const next = { ...prev };
      mods.forEach(m => { next[m] = level; });
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTenant) {
      onError?.('Please select a tenant');
      return;
    }

    setSubmitting(true);
    try {
      // Only enrolled modules go over the wire — the endpoint upserts exactly
      // what is posted, so omitted modules stay untouched.
      const payload = Object.fromEntries(
        Object.entries(permissions).filter(([m]) => mods.includes(m))
      );
      await userService.assignTenant(user.username || user.email, selectedTenant, payload);
      onSuccess?.('User assigned to tenant successfully');
    } catch (err) {
      onError?.(err.response?.data?.error || 'Failed to assign user to tenant');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Assign to Tenant</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p>Assign <strong>{user?.name || user?.email}</strong> to a tenant with module permissions.</p>
            
            <div className="form-group">
              <label>Search Tenants</label>
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by name..."
              />
            </div>

            <div className="form-group">
              <label>Select Tenant *</label>
              {loading ? (
                <div className="loading-inline">Loading tenants...</div>
              ) : tenants.length === 0 ? (
                <div className="empty-state-small">No tenants found</div>
              ) : (
                <select
                  value={selectedTenant}
                  onChange={e => setSelectedTenant(e.target.value)}
                  required
                >
                  <option value="">-- Select a tenant --</option>
                  {tenants.map(tenant => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="form-group">
              <label>Module Permissions</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.85rem', color: '#666' }}>Quick set:</span>
                {LEVELS.map(level => (
                  <button key={level} type="button" className="btn btn-secondary btn-sm"
                    onClick={() => setAllPermissions(level)} style={{ textTransform: 'capitalize' }}>
                    All {level}
                  </button>
                ))}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Module</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Permission Level</th>
                  </tr>
                </thead>
                <tbody>
                  {mods.map(mod => (
                    <tr key={mod} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '0.5rem', fontWeight: 500 }}>
                        {MODULE_LABELS[mod] || mod}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <select
                          value={permissions[mod]}
                          onChange={e => handlePermissionChange(mod, e.target.value)}
                          style={{ width: '100%' }}
                        >
                          {LEVELS.map(level => (
                            <option key={level} value={level}>{level}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting || !selectedTenant}>
              {submitting ? 'Assigning...' : 'Assign to Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Product vocabulary (V276) ──
// Tenants enroll in PRODUCTS (product_t: brands_manager | media_console |
// gridrock); permissions stay keyed by MODULE ('campaigns'/'devices'/'account').
// These maps translate between the two for display only — never send a
// product_t value to a permissions endpoint.
export const PRODUCT_META = {
  brands_manager: { label: 'Brands Manager', short: 'Brands', bg: '#fff9e6', fg: '#b8860b' },
  media_console: { label: 'Media Console', short: 'Media', bg: '#e3f2fd', fg: '#1565c0' },
  gridrock: { label: 'GridRock', short: 'GridRock', bg: '#f3e5f5', fg: '#7b1fa2' },
};

// Product-facing names for the raw module keys. 'account' is not a product —
// it is workspace administration, present on every tenant regardless of
// enrollment — so it gets its own label rather than a PRODUCT_META entry.
export const MODULE_LABELS = {
  campaigns: 'Brands Manager',
  devices: 'Media Console',
  account: 'Workspace Admin',
};

// Small inline-styled chip for a product_t value. Deliberately mirrors the
// signup-channel badge geometry in Users.js (compact = fontSize 0.65rem,
// padding 1px 5px) so table rows read as one system. Inline styles, not a CSS
// class: every small colored chip in this app is inline-styled.
export const ProductChip = ({ product, compact }) => {
  const m = PRODUCT_META[product] || { label: product, short: product, bg: '#f5f5f5', fg: '#666' };
  return (
    <span title={m.label} style={{
      display: 'inline-block',
      fontSize: compact ? '0.65rem' : '0.7rem',
      padding: compact ? '1px 5px' : '2px 6px',
      borderRadius: 4,
      background: m.bg,
      color: m.fg,
      marginRight: 4,
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
    }}>
      {m.short}
    </span>
  );
};

// Permission badges for display
const PermissionBadge = ({ module, level }) => {
  const colors = { admin: '#dc3545', editor: '#fd7e14', viewer: '#6c757d' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '0.75rem',
      marginRight: '4px',
      background: `${colors[level] || '#6c757d'}15`,
      color: colors[level] || '#6c757d',
      border: `1px solid ${colors[level] || '#6c757d'}30`,
    }}>
      {MODULE_LABELS[module] || module}: {level}
    </span>
  );
};

// Tenant Badge Component for user list
export const TenantBadge = ({ count, tenants }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  if (count === 0) {
    return <span className="tenant-badge tenant-none">No tenants</span>;
  }

  return (
    <div 
      className="tenant-badge-wrapper"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span className="tenant-badge">
        {count} tenant{count !== 1 ? 's' : ''}
      </span>
      {showTooltip && tenants && tenants.length > 0 && (
        <div className="tenant-tooltip">
          {tenants.map((t, i) => (
            <div key={i} className="tenant-tooltip-item">
              <strong>{t.tenant_name}</strong>
              <div style={{ marginTop: '2px' }}>
                {t.permissions ? (
                  Object.entries(t.permissions).map(([mod, level]) => (
                    <PermissionBadge key={mod} module={mod} level={level} />
                  ))
                ) : (
                  <span className="role-badge">{t.role || 'no permissions'}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Edit Permissions Modal — edit existing user-tenant permissions
export const EditPermissionsModal = ({ user, assignment, onClose, onSuccess, onError }) => {
  // Seed absent modules as No-access, not viewer: the editor must show what is
  // STORED, and (now that the diff below routes leveled modules through the
  // upsert) a viewer seed on an absent module would silently grant it on an
  // untouched save.
  const [permissions, setPermissions] = useState({
    campaigns: assignment?.permissions?.campaigns || NO_ACCESS,
    devices: assignment?.permissions?.devices || NO_ACCESS,
    account: assignment?.permissions?.account || NO_ACCESS,
  });
  const [submitting, setSubmitting] = useState(false);

  // Enrollment-aware editor: GET /users/:id/tenants rows carry products
  // (V276). A row without the field is a stale payload and fails closed to
  // account-only — see enrolledModulesFromProducts.
  const mods = enrolledModulesFromProducts(assignment?.products);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const setAllPermissions = (level) => {
    setPermissions(prev => {
      const next = { ...prev };
      mods.forEach(m => { next[m] = level; });
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // The mods filter stays load-bearing: saving a member of a
      // partially-enrolled tenant must never write (or revoke) the hidden
      // modules the enrollment gate removed. On top of it, diff against the
      // member's CURRENT permissions. The upsert endpoint writes only the
      // modules posted and cannot delete a row — the per-module DELETE is the
      // revoke path — so the save splits three ways:
      //   No-access + row EXISTS   → DELETE that module row (sequentially),
      //   No-access + row absent   → nothing to do on either endpoint,
      //   leveled                  → existing upsert POST (ON CONFLICT), as
      //                              before.
      const uid = user.id || user.username || user.email;
      const desired = Object.entries(permissions).filter(([m]) => mods.includes(m));
      const toRevoke = desired
        .filter(([m, level]) => level === NO_ACCESS && assignment?.permissions?.[m])
        .map(([m]) => m);
      const payload = Object.fromEntries(desired.filter(([, level]) => level !== NO_ACCESS));
      for (const mod of toRevoke) {
        try {
          await userService.removeModulePermission(uid, assignment.tenant_id, mod);
        } catch (err) {
          // 404 = the row is already gone (concurrent revoke) — the end state
          // this save wanted; anything else aborts the save.
          if (err.response?.status !== 404) throw err;
        }
      }
      if (Object.keys(payload).length > 0) {
        await userService.assignTenant(uid, assignment.tenant_id, payload);
      }
      onSuccess?.('Permissions updated successfully');
    } catch (err) {
      onError?.(err.response?.data?.error || 'Failed to update permissions');
    } finally {
      setSubmitting(false);
    }
  };

  const permColors = { admin: '#dc3545', editor: '#fd7e14', viewer: '#6c757d' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <h2>Edit Permissions</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>{user?.name || user?.email}</strong> → <strong>{assignment.tenant_name}</strong>
            </p>

            {/* Current permissions. Dormant grants (module exists in the DB
                but its product is not enrolled) stay VISIBLE here, greyed —
                the operator console tells the truth about what is stored,
                unlike member badges which show only what is live. The editor
                below still neither renders nor writes un-enrolled modules. */}
            <div style={{ background: '#f8f9fa', padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>
              <span style={{ color: '#666' }}>Current: </span>
              {assignment.permissions && Object.entries(assignment.permissions).map(([mod, level]) => {
                const enrolled = mods.includes(mod);
                return (
                  <span key={mod}
                    title={enrolled ? undefined : 'Not enrolled — this grant is dormant'}
                    style={{
                      display: 'inline-block', padding: '1px 5px', borderRadius: '3px', marginRight: '4px',
                      background: enrolled ? `${permColors[level]}15` : '#ececec',
                      color: enrolled ? permColors[level] : '#999', fontSize: '0.75rem',
                      border: `1px solid ${enrolled ? `${permColors[level]}30` : '#ddd'}`,
                    }}>
                    {MODULE_LABELS[mod] || mod}: {level}
                  </span>
                );
              })}
            </div>

            <div className="form-group">
              <label>New Permissions</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.85rem', color: '#666' }}>Quick set:</span>
                {LEVELS.map(level => (
                  <button key={level} type="button" className="btn btn-secondary btn-sm"
                    onClick={() => setAllPermissions(level)} style={{ textTransform: 'capitalize' }}>
                    All {level}
                  </button>
                ))}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Module</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Permission Level</th>
                  </tr>
                </thead>
                <tbody>
                  {mods.map(mod => (
                    <tr key={mod} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '0.5rem', fontWeight: 500 }}>
                        {MODULE_LABELS[mod] || mod}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <select value={permissions[mod]}
                          onChange={e => setPermissions(prev => ({ ...prev, [mod]: e.target.value }))}
                          style={{ width: '100%' }}>
                          {LEVELS.map(level => <option key={level} value={level}>{level}</option>)}
                          <option value={NO_ACCESS}>No access</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Update Permissions'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TenantAssignmentModal;
